/**
 * networkUtils.ts
 * Utilitas untuk mendeteksi koneksi lambat/putus dan
 * mengklasifikasikan error secara tepat (network vs server).
 */

// ─── Tipe Error ───────────────────────────────────────────────────────────────

export type NetworkErrorType =
  | 'offline'        // Tidak ada koneksi internet sama sekali
  | 'timeout'        // Request timeout (koneksi lambat)
  | 'slow_connection'// Koneksi terdeteksi lambat
  | 'server_error'   // Error dari server (5xx)
  | 'auth_error'     // Error autentikasi (401/403)
  | 'not_found'      // Data tidak ditemukan (404)
  | 'unknown';       // Error lain

export interface ClassifiedError {
  type: NetworkErrorType;
  message: string;        // Pesan teknis (untuk log)
  userMessage: string;    // Pesan yang ditampilkan ke user
  canRetry: boolean;
  isNetworkIssue: boolean;
}

// ─── Deteksi Koneksi ──────────────────────────────────────────────────────────

/**
 * Mengecek apakah browser sedang online
 */
export function isOnline(): boolean {
  return navigator.onLine;
}

/**
 * Mengecek kualitas koneksi menggunakan Network Information API (jika tersedia).
 * Mengembalikan true jika koneksi terdeteksi lambat.
 */
export function isSlowConnection(): boolean {
  const nav = navigator as Navigator & {
    connection?: {
      effectiveType?: string;
      downlink?: number;
      rtt?: number;
    };
  };
  const conn = nav.connection;
  if (!conn) return false;

  // effectiveType: 'slow-2g' | '2g' | '3g' | '4g'
  if (conn.effectiveType && ['slow-2g', '2g'].includes(conn.effectiveType)) {
    return true;
  }
  // downlink dalam Mbps — di bawah 1 Mbps dianggap lambat
  if (conn.downlink !== undefined && conn.downlink < 1) {
    return true;
  }
  // RTT di atas 500ms dianggap lambat
  if (conn.rtt !== undefined && conn.rtt > 500) {
    return true;
  }
  return false;
}

// ─── Klasifikasi Error ────────────────────────────────────────────────────────

/**
 * Mengklasifikasikan error dari fetch/API menjadi tipe yang bermakna bagi user.
 */
export function classifyError(
  error: unknown,
  httpStatus?: number
): ClassifiedError {

  // 1. Tidak ada koneksi
  if (!isOnline()) {
    return {
      type: 'offline',
      message: 'Browser offline (navigator.onLine = false)',
      userMessage: 'Kamu sedang offline. Periksa koneksi internetmu dan coba lagi.',
      canRetry: true,
      isNetworkIssue: true,
    };
  }

  // 2. Error jaringan (TypeError: Failed to fetch, dll.)
  if (error instanceof TypeError) {
    const msg = error.message.toLowerCase();
    const isNetworkMsg =
      msg.includes('failed to fetch') ||
      msg.includes('network request failed') ||
      msg.includes('fetch') ||
      msg.includes('load failed') ||
      msg.includes('networkerror');

    if (isNetworkMsg) {
      const slow = isSlowConnection();
      return {
        type: slow ? 'slow_connection' : 'timeout',
        message: `Network TypeError: ${error.message}`,
        userMessage: slow
          ? 'Koneksi internetmu terdeteksi sangat lambat. Data gagal dimuat. Coba gunakan koneksi yang lebih stabil atau refresh halaman.'
          : 'Gagal menghubungi server. Pastikan koneksi internetmu stabil, lalu refresh halaman.',
        canRetry: true,
        isNetworkIssue: true,
      };
    }
  }

  // 3. Error berdasarkan HTTP status code
  if (httpStatus !== undefined) {
    if (httpStatus === 401 || httpStatus === 403) {
      return {
        type: 'auth_error',
        message: `HTTP ${httpStatus} Auth Error`,
        userMessage: 'Sesi kamu telah berakhir. Silakan login ulang.',
        canRetry: false,
        isNetworkIssue: false,
      };
    }
    if (httpStatus === 404) {
      return {
        type: 'not_found',
        message: `HTTP 404 Not Found`,
        userMessage: 'Data tidak ditemukan.',
        canRetry: false,
        isNetworkIssue: false,
      };
    }
    if (httpStatus >= 500) {
      return {
        type: 'server_error',
        message: `HTTP ${httpStatus} Server Error`,
        userMessage: 'Server sedang mengalami gangguan. Tim kami sedang menangani masalah ini. Silakan coba beberapa saat lagi.',
        canRetry: true,
        isNetworkIssue: false,
      };
    }
    if (httpStatus === 408 || httpStatus === 504) {
      return {
        type: 'timeout',
        message: `HTTP ${httpStatus} Timeout`,
        userMessage: 'Koneksi timeout. Koneksi internetmu mungkin lambat atau tidak stabil. Coba refresh halaman.',
        canRetry: true,
        isNetworkIssue: true,
      };
    }
  }

  // 4. Error dengan nama 'AbortError' (fetch dibatalkan karena timeout manual)
  if (error instanceof DOMException && error.name === 'AbortError') {
    return {
      type: 'timeout',
      message: 'Request aborted (timeout)',
      userMessage: 'Permintaan memakan terlalu lama. Koneksimu mungkin lambat. Coba lagi dengan koneksi yang lebih stabil.',
      canRetry: true,
      isNetworkIssue: true,
    };
  }

  // 5. Fallback
  return {
    type: 'unknown',
    message: error instanceof Error ? error.message : String(error),
    userMessage: 'Terjadi kesalahan yang tidak terduga. Silakan coba lagi.',
    canRetry: true,
    isNetworkIssue: false,
  };
}

// ─── Helper: Fetch dengan Timeout & Auto-Classify ────────────────────────────

/**
 * Wrapper fetch yang otomatis:
 * - Menambahkan timeout (default 15 detik)
 * - Mengklasifikasikan error menjadi NetworkError yang informatif
 * - Mengembalikan data atau melempar ClassifiedError
 */
export async function fetchWithTimeout<T = unknown>(
  url: string,
  options: RequestInit = {},
  timeoutMs = 15000
): Promise<{ data: T; status: number }> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

  try {
    const response = await fetch(url, {
      ...options,
      signal: controller.signal,
    });
    clearTimeout(timeoutId);

    if (!response.ok) {
      const classified = classifyError(null, response.status);
      throw classified;
    }

    const data = await response.json() as T;
    return { data, status: response.status };
  } catch (err) {
    clearTimeout(timeoutId);

    // Jika sudah ClassifiedError, teruskan
    if (err && typeof err === 'object' && 'type' in err && 'userMessage' in err) {
      throw err;
    }

    // Klasifikasikan error mentah
    throw classifyError(err);
  }
}

// ─── Helper: Cek apakah suatu error adalah masalah jaringan ──────────────────

export function isNetworkError(error: unknown): boolean {
  if (!isOnline()) return true;
  if (error instanceof TypeError) {
    const msg = error.message.toLowerCase();
    return (
      msg.includes('failed to fetch') ||
      msg.includes('network') ||
      msg.includes('fetch')
    );
  }
  if (error instanceof DOMException && error.name === 'AbortError') return true;
  if (error && typeof error === 'object' && 'isNetworkIssue' in error) {
    return (error as ClassifiedError).isNetworkIssue;
  }
  return false;
}

/**
 * Menentukan apakah error layak di-retry oleh React Query
 */
export function shouldRetryOnError(error: unknown): boolean {
  if (error && typeof error === 'object' && 'canRetry' in error) {
    return (error as ClassifiedError).canRetry;
  }
  return isNetworkError(error);
}

/**
 * Mendapatkan pesan ramah dari error apapun
 */
export function getFriendlyErrorMessage(error: unknown): string {
  if (!isOnline()) {
    return 'Kamu sedang offline. Periksa koneksi internetmu dan coba lagi.';
  }
  if (error && typeof error === 'object' && 'userMessage' in error) {
    return (error as ClassifiedError).userMessage;
  }
  if (isNetworkError(error)) {
    return isSlowConnection()
      ? 'Koneksi internetmu sangat lambat. Data gagal dimuat. Coba gunakan koneksi yang lebih stabil atau refresh halaman.'
      : 'Gagal memuat data. Periksa koneksi internetmu dan refresh halaman.';
  }
  if (error instanceof Error) {
    return error.message;
  }
  return 'Terjadi kesalahan. Silakan coba lagi.';
}
